<template>
  <div>
    <div class="task-board" :data-board-name="getBoardName(index)">
      <input
        type="text"
        class="form-control"
        :value="list.column.name"
        v-if="isEditing"
        @blur="saveTaskListName"
      />
      <div v-if="!isEditing" class="board-header">
        <p class="board-name">{{ list.column.name }}</p>
        <div class="card-header">
          <div class="card-data-tickets">
            <p class="total">{{ list.column.meta.total_count }}&nbsp;</p>

            <p class="tickets" v-if="!isFunnelSale">{{ ticketsLabel }}</p>
            <p class="tickets" v-if="isFunnelSale">{{ ticketsLabel }}:&nbsp;</p>

            <p class="money" v-if="isFunnelSale">
              {{ formatCurrency(valueCollum) }}
            </p>
          </div>
        </div>
      </div>
      <div class="board-content" @scroll="onScroll">
        <ul class="task-list">
          <draggable
            v-model="list.column.tickets"
            v-bind="dragOptions"
            :disabled="selectedTicketIds.length > 0"
            class="list-group"
            :empty-insert-threshold="5"
            @start="onDragStart"
            @end="onDragEnd"
            @change="onTaskDrop"
          >
            <TaskItem
              v-for="item in list.column.tickets"
              :item="item"
              :list="list"
              :kanban-id="kanbanId"
              :board="board"
              :key="item.id"
              :isFunnelSale="isFunnelSale"
              :is-selected="selectedTicketIds.includes(item.id)"
              :is-selection-disabled="isSelectionDisabledForColumn"
              :selection-mode="selectionMode"
              @toggle-selection="handleToggleSelection"
              @ticket-removed="removeTicketFromColumn"
            />
            <li
              v-if="list.column.tickets.length === 0"
              class="list-group-item placeholder-item"
            >
              <span>{{ this.$t('TICKET.DRAG_TICKET') }}</span>
            </li>
          </draggable>
        </ul>
        <div v-if="loadingMore" class="text-center">
          <span class="spinner" />
        </div>
      </div>
    </div>

    <modal-win-lose-choice
      v-if="isBusinessOpportunity"
      :show="isDragging"
      :ticket="draggedTicket"
      @win="handleDropWin"
      @lose="handleDropLose"
    />

    <modal-lose-ticket
      v-if="showLoseModal"
      :show="showLoseModal"
      :ticket="pendingTicket"
      @cancel="handleCancelLose"
      @success="handleLoseSuccess"
    />
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import TaskItem from './TaskItem';
import ModalWinLoseChoice from './ModalWinLoseChoice.vue';
import ModalLoseTicket from '../../../conversation/contact/ModalLoseTicket.vue';
import { mapGetters } from 'vuex';
import { formatCurrency } from '../../../../../helper/formatCurrency';
import alertMixin from 'shared/mixins/alertMixin';

export default {
  components: {
    TaskItem,
    draggable,
    ModalWinLoseChoice,
    ModalLoseTicket,
  },
  mixins: [alertMixin],
  props: [
    'board',
    'list',
    'index',
    'kanbanId',
    'filters',
    'selectedTicketIds',
    'lockedSelectionColumnId',
    'selectionMode',
  ],
  data() {
    return {
      drag: false,
      isEditing: false,
      taskListName: this.list.name,
      colors: ['primary', 'secondary', 'tertiary', 'quaternary'],
      loadingMore: false,
      itemsPerPage: 10,
      page: 1,
      nextUnloadedIndex: this.list.column.tickets.length,
      allItemsLoaded: false,
      isDragging: false,
      showLoseModal: false,
      draggedTicket: null,
      pendingTicket: null,
      dragOptions: {
        group: 'tasks',
        animation: 200,
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        dragClass: 'sortable-drag',
        emptyInsertThreshold: 5,
        filter: '.ticket-select-checkbox',
      },
    };
  },
  computed: {
    ...mapGetters({
      kanbanList: 'getKanbanfunnels',
    }),
    items() {
      return this.list.column.tickets;
    },
    isFunnelSale() {
      const kanban = this.kanbanList.find(obj => obj.id == this.kanbanId);
      return kanban?.enable_sales_funnel || false;
    },

    isBusinessOpportunity() {
      const kanban = this.kanbanList.find(obj => obj.id == this.kanbanId);
      return (
        kanban?.type_opportunity === 'business' ||
        kanban?.type_opportunity === 'both'
      );
    },

    ticketsLabel() {
      const kanban = this.kanbanList.find(obj => obj.id == this.kanbanId);
      const isBusinessOpportunity =
        kanban?.type_opportunity === 'business' ||
        kanban?.type_opportunity === 'both';
      return isBusinessOpportunity
        ? this.$t('TICKET.BUSINESS_TITLE')
        : this.$t('TICKET.TITLE');
    },

    valueCollum() {
      if (
        this.list.column.meta.total_count > 0 &&
        this.list.column.meta.total_sales_value > 0
      ) {
        return this.list.column.meta.total_sales_value;
      }
      return 0;
    },
    isSelectionDisabledForColumn() {
      return (
        !!this.lockedSelectionColumnId &&
        this.lockedSelectionColumnId !== this.list.column.id
      );
    },
  },
  methods: {
    formatCurrency,

    handleToggleSelection({ ticket, selected }) {
      this.$emit('toggle-ticket-selection', {
        ticketId: ticket.id,
        selected,
        sourceColumnId: this.list.column.id,
      });
    },

    getBoardName(index) {
      return this.colors[index % this.colors.length];
    },
    saveTaskListName(e) {
      this.list.column.name = e.target.value;
      this.isEditing = !this.isEditing;
    },

    onDragStart(event) {
      this.isDragging = true;
      const ticketId = parseInt(
        event.item.querySelector('[data-ticket-id]')?.dataset?.ticketId
      );
      if (ticketId) {
        this.draggedTicket = this.board
          .flatMap(col => col.column.tickets)
          .find(t => t.id === ticketId);
      }
    },

    onDragEnd() {
      if (!this.showLoseModal) {
        this.isDragging = false;
      }
    },

    async onTaskDrop(event) {
      if (event.added) {
        const movedTicket = event.added.element;
        const destinationColumn = this.board.find(column =>
          column.column.tickets.includes(movedTicket)
        );
        const originColumn = this.board.find(
          column => column.column.id === movedTicket.columns_id
        );
        await this.processTicketMove(
          movedTicket,
          originColumn,
          destinationColumn
        );
        const totalAfterMove = Number(this.list.column.meta.total_count || 0);
        if (this.nextUnloadedIndex < totalAfterMove) {
          this.allItemsLoaded = false;
        }
      } else if (event.removed) {
        this.fillColumnAfterMoveOut(event.removed.element?.id);
      }
    },

    async processTicketMove(movedTicket, originColumn, destinationColumn) {
      movedTicket.columns_id = destinationColumn?.column.id;
      await this.$store.dispatch('updateTicket', movedTicket);

      if (destinationColumn) {
        destinationColumn.column.meta.total_count =
          Number(destinationColumn.column.meta.total_count || 0) + 1;
        this.$set(
          destinationColumn.column.meta,
          'total_sales_value',
          destinationColumn.column.meta.total_sales_value +
            Number(movedTicket.sale_value || 0)
        );
      }

      if (originColumn) {
        originColumn.column.meta.total_count = Math.max(
          0,
          Number(originColumn.column.meta.total_count || 0) - 1
        );
        this.$set(
          originColumn.column.meta,
          'total_sales_value',
          originColumn.column.meta.total_sales_value -
            Number(movedTicket.sale_value || 0)
        );
      }
    },

    async handleDropWin() {
      try {
        if (!this.draggedTicket) return;
        const ticket = this.draggedTicket;
        const originColumn = this.board.find(
          col => col.column.id === ticket.columns_id
        );

        ticket.status = 'gain';
        await this.$store.dispatch('updateTicket', ticket);

        if (originColumn) {
          const idx = originColumn.column.tickets.findIndex(
            t => t.id === ticket.id
          );
          if (idx > -1) {
            originColumn.column.tickets.splice(idx, 1);
            originColumn.column.meta.total_count = Math.max(
              0,
              Number(originColumn.column.meta.total_count || 0) - 1
            );
            this.$set(
              originColumn.column.meta,
              'total_sales_value',
              originColumn.column.meta.total_sales_value -
                Number(ticket.sale_value || 0)
            );
          }
        }

        this.showAlert(this.$t('TICKET.WIN.BUSINESS_SUCCESS'));
        this.resetPendingState();
      } catch (error) {
        console.error('Error handling win:', error);
        this.showAlert(this.$t('TICKET.WIN.BUSINESS_ERROR'));
      }
    },

    handleDropLose() {
      if (!this.draggedTicket) return;
      this.pendingTicket = { ...this.draggedTicket };
      this.isDragging = false;
      this.showLoseModal = true;
    },

    handleCancelLose() {
      this.showLoseModal = false;
      this.resetPendingState();
    },

    async handleLoseSuccess() {
      if (this.pendingTicket) {
        const ticketId = this.pendingTicket.id;
        for (const col of this.board) {
          const idx = col.column.tickets.findIndex(t => t.id === ticketId);
          if (idx > -1) {
            const saleValue = Number(col.column.tickets[idx].sale_value || 0);
            col.column.tickets.splice(idx, 1);
            col.column.meta.total_count = Math.max(
              0,
              Number(col.column.meta.total_count || 0) - 1
            );
            this.$set(
              col.column.meta,
              'total_sales_value',
              col.column.meta.total_sales_value - saleValue
            );
            break;
          }
        }
      }

      this.showLoseModal = false;
      this.resetPendingState();
    },

    resetPendingState() {
      this.pendingTicket = null;
      this.draggedTicket = null;
      this.isDragging = false;
    },

    removeTicketFromColumn(ticketId) {
      const idx = this.list.column.tickets.findIndex(t => t.id === ticketId);
      if (idx > -1) {
        const saleValue = Number(this.list.column.tickets[idx].sale_value || 0);
        this.list.column.tickets.splice(idx, 1);
        this.list.column.meta.total_count = Math.max(0, this.list.column.meta.total_count - 1);
        this.$set(
          this.list.column.meta,
          'total_sales_value',
          (this.list.column.meta.total_sales_value || 0) - saleValue
        );
      }
    },
    onScroll(event) {
      const bottomOfWindow =
        Math.ceil(event.target.scrollTop + event.target.clientHeight) >=
        event.target.scrollHeight;

      if (bottomOfWindow && !this.loadingMore && !this.allItemsLoaded) {
        this.loadMoreItems();
      }
    },
    async loadMoreItems() {
      this.loadingMore = true;

      try {
        const nextPage = this.page + 1;
        const newItems = await this.fetchMoreItems(nextPage);
        const currentIds = new Set(
          this.list.column.tickets.map(ticket => ticket.id)
        );
        const uniqueItems = newItems.filter(
          ticket => !currentIds.has(ticket.id)
        );

        if (uniqueItems.length > 0) {
          this.page = nextPage;
          this.nextUnloadedIndex += uniqueItems.length;
          this.list.column.tickets = [
            ...this.list.column.tickets,
            ...uniqueItems,
          ];
        } else {
          this.allItemsLoaded = true;
        }
      } catch (error) {
        this.showAlert(this.$t('TICKET.CREATE.API.ERROR_MESSAGE'));
      } finally {
        this.loadingMore = false;
      }
    },
    async fillColumnAfterMoveOut(removedTicketId = null) {
      await this.$nextTick();

      try {
        const params = {
          kanbanfunnel_id: this.kanbanId,
          columns_id: this.list.column.id,
          per_page: this.itemsPerPage,
          page: 1,
          ...(this.filters || {}),
        };

        const resp = await this.$store.dispatch(
          'getTicketsByColumnColumns',
          params
        );
        const snapshotTickets = resp?.tickets || [];

        const uniqueTickets = snapshotTickets.filter((ticket, index, self) => {
          if (!ticket) {
            return false;
          }
          if (removedTicketId && ticket.id === removedTicketId) {
            return false;
          }
          return self.findIndex(item => item.id === ticket.id) === index;
        });

        this.list.column.tickets = uniqueTickets;
        this.page = 1;
        this.nextUnloadedIndex = uniqueTickets.length;

        const totalTickets = Number(this.list.column.meta.total_count || 0);
        this.allItemsLoaded = this.nextUnloadedIndex >= totalTickets;
      } catch (error) {
        this.showAlert(this.$t('TICKET.CREATE.API.ERROR_MESSAGE'));
      }
    },
    async fetchMoreItems(page) {
      if (page > this.list.column.meta.total_pages) {
        return [];
      }

      const params = {
        kanbanfunnel_id: this.kanbanId,
        columns_id: this.list.column.id,
        per_page: 10,
        page,
        ...(this.filters || {}),
      };

      const resp = await this.$store.dispatch(
        'getTicketsByColumnColumns',
        params
      );
      return resp.tickets;
    },
  },
  watch: {
    'list.column.id': {
      immediate: true,
      handler() {
        this.page = 1;
        this.nextUnloadedIndex = this.list.column.tickets.length;
        this.allItemsLoaded =
          this.nextUnloadedIndex >=
          Number(this.list.column.meta.total_count || 0);
      },
    },
    'list.column.tickets.length'(newLength) {
      if (newLength > this.nextUnloadedIndex) {
        this.nextUnloadedIndex = newLength;
      }
    },
  },
};
</script>

<style>
.sortable-chosen.ghost .task-item {
  background: repeating-linear-gradient(
    145deg,
    transparent,
    transparent 5px,
    #e8eaf1 5px,
    #e8eaf1 10px
  );
  border: 2px solid #e2e2e2;
}

.card-data-tickets {
  display: flex;
  align-items: center;
}
.money,
.total {
  font-weight: bold;
}
.money {
  color: #4caf50;
}

.card-data-tickets p {
  margin: 0;
}

.flip-list-move {
  transition: transform 0.2s;
}

.no-move {
  transition: transform 0s;
}

.list-group {
  min-height: 20px;
}

.list-group-item {
  cursor: move;
}

.list-group-item i {
  cursor: pointer;
}

.board-header {
  border-bottom: 1px solid var(--s-100);
}

.task-board .board-header .options {
  padding: 5px 5px;
  display: flex;
  justify-content: space-between;
}

.task-board .board-header .dropdown-menu {
  min-width: 10rem;
}

.task-board .board-content {
  max-height: 75vh;
  overflow-y: auto;
}

.placeholder-item {
  min-height: 50px;
  text-align: center;
  line-height: 50px;
  color: #999;
  border: 1px dashed #ccc;
}

.card-header {
  margin-bottom: 1rem;
}
</style>
